if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');  
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.sanitizeInterlinkingSwatches();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);          
          this.bindInterlinkButtons();

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');      
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }

    /**
     * Turn interlinking swatch labels into buttons and remove their inputs
     * to avoid triggering the legacy redirect script and radio behavior.
     */
    sanitizeInterlinkingSwatches() {
      if (!this.productElement) return;
      
      const swatches = this.productElement.querySelectorAll('.variant-swatch label[data-interlink]');

      swatches.forEach(label => {
        const wrapper = label.closest('.variant-swatch');
        if (!wrapper) return;

        const input = wrapper.querySelector('input[type="radio"]');
        const button = document.createElement('button');
        button.className = label.className;

        for (const attr of label.attributes) {
          if (attr.name.startsWith('data-')) {
            button.setAttribute(attr.name, attr.value);
          }
        }
        
        button.innerHTML = label.innerHTML;
        button.type = 'button';
        
        label.replaceWith(button);
        if (input) input.remove();
      });
    }

    /**
     * Attach click handlers to interlink buttons inside the modal
     * Each button uses data-interlink as the product handle.
     */
    bindInterlinkButtons() {
      if (!this.modalContent) return;

      const buttons = this.modalContent.querySelectorAll('button[data-interlink]');
      if (!buttons.length) return;

      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const handle = btn.getAttribute('data-interlink');
          if (!handle) return;

          this.loadInterlinkedProduct(handle);
        });
      });
    }

    /**
     * Load another product into the existing Quick Add modal,
     * based on a product handle from data-interlink.
     */
    loadInterlinkedProduct(handle) {
      // If your quick-add uses a view, keep ?view=quick-add
      const url = `/products/${handle}?view=quick-add`;

      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          if (!this.productElement) return;

          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.sanitizeInterlinkingSwatches();

          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          // Re-bind interlink buttons for the newly loaded product
          this.bindInterlinkButtons();

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
        })
        .catch((error) => {
          console.error('Error loading interlinked product in quick-add modal:', error);
        });
    }
  });
}
